import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  EmptyState,
  MasterList,
  PageHead,
  ProgressBar,
  Skeleton,
  useToast,
  type MasterItem,
} from "@guestpilot/ui-kit";
import { useGet, useWrite } from "../../api/hooks";
import type { Schema } from "../../api/types";
import { fieldErrorsOf, useHandleApiError } from "../../errors";
import { formatDate } from "../../format";
import { useI18n } from "../../i18n";
import { useSession } from "../../session";
import { DecisionForm, type Decision } from "./DecisionForm";

type Item = Schema<"ReviewItem">;
type Campaign = Schema<"Campaign">;

const OUTCOME: Record<string, string> = {
  keep: "keep",
  extend: "extend",
  remove: "remove_requested",
  exception: "exception",
  transfer_sponsor: "transfer_sponsor",
};

/** Master list plus detail: one guest at a time. Opening from a notification selects the first undecided item (team-tab.md Review). */
export function Review({ teamId }: { teamId: string }) {
  const { campaignId = "" } = useParams();
  const { t, locale } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const tz = s.me.tenant.timeZone;
  const campaign = useGet<Campaign>(`/api/v1/campaigns/${campaignId}`);
  const items = useGet<Item[]>(`/api/v1/campaigns/${campaignId}/items`);
  const decide = useWrite<Item, Decision>("POST", "/api/v1/review/items/:id/decisions", [
    `/api/v1/campaigns`,
    "/api/v1/teams",
    "/api/v1/dashboard",
    "/api/v1/removals",
    "/api/v1/exceptions",
    "/api/v1/participants",
  ]);
  const [selected, setSelected] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const list = useMemo(
    () => (items.data ?? []).filter((i) => i.participant?.teamId === teamId || !teamId),
    [items.data, teamId],
  );
  const firstOpen = list.find((i) => i.state === "open")?.id ?? null;
  useEffect(() => {
    if (selected === null && list.length > 0) setSelected(firstOpen ?? list[0]?.id ?? null);
  }, [list, firstOpen, selected]);

  if (items.isLoading || campaign.isLoading) return <Skeleton rows={6} />;
  if (list.length === 0) return <EmptyState title={t("ui:review.empty")} />;
  const total = list.length;
  const resolved = list.filter((i) => i.state !== "open").length;
  const masterItems: MasterItem[] = list.map((i) => ({
    id: i.id ?? "",
    label: i.participant?.displayName || i.participant?.email || "",
    sub: i.participant?.domain,
    state: i.state === "open" ? (i.id === firstOpen ? "active" : "pending") : "done",
    outcome:
      i.state === "decided"
        ? t(`ui:outcome.${OUTCOME[i.currentDecision?.decision ?? "keep"] ?? "keep"}`)
        : i.state === "open"
          ? i.id === firstOpen
            ? t("ui:review.reviewing")
            : t("ui:review.notYet")
          : t(`ui:review.state.${i.state}`),
  }));
  const current = list.find((i) => i.id === selected) ?? list[0]!;
  const closed = campaign.data?.status !== "in_progress";

  return (
    <div className="gp-review" style={{ minHeight: "60vh" }}>
      <MasterList
        label={t("ui:review.list")}
        items={masterItems}
        selectedId={current.id ?? null}
        onSelect={(id) => {
          setSelected(id);
          setErrors({});
        }}
        doneLabel={t("ui:review.done")}
      />
      <div className="gp-review-detail">
        <PageHead
          title={campaign.data?.name ?? ""}
          subtitle={t("ui:review.due", {
            date: formatDate(campaign.data?.dueAt, locale, tz),
            by: campaign.data?.launchedBy?.displayName ?? "",
          })}
        />
        <ProgressBar value={resolved} max={total} label={t("ui:review.progress", { n: resolved, total })} />
        <span className="t-body-sm gp-muted">{t("ui:review.progress", { n: resolved, total })}</span>
        {current.participant ? (
          <DecisionForm
            key={`${current.id}-${current.version}`}
            participant={current.participant}
            itemVersion={current.version}
            decided={
              current.state === "decided" ? OUTCOME[current.currentDecision?.decision ?? "keep"] : undefined
            }
            serverErrors={errors}
            busy={decide.isPending}
            gateReason={closed ? t("ui:review.closed") : s.gate("REVIEW_DECIDE", teamId)}
            onSubmit={(d) =>
              decide.mutate(
                { params: { id: current.id ?? "" }, body: d },
                {
                  onSuccess: () => {
                    setErrors({});
                    toast.success(t("ui:review.saved"));
                    const idx = list.findIndex((i) => i.id === current.id);
                    const next =
                      list.slice(idx + 1).find((i) => i.state === "open") ??
                      list.find((i) => i.state === "open" && i.id !== current.id);
                    if (next) setSelected(next.id ?? null);
                  },
                  onError: (e) => {
                    setErrors(fieldErrorsOf(e));
                    fail(e);
                  },
                },
              )
            }
          />
        ) : null}
      </div>
    </div>
  );
}
